/**
 * Date formatting utilities for trial date display.
 * 
 * Rules:
 * - Always include the year
 * - Single day: "Mar 20, 2026"
 * - Same month: "Mar 20-22, 2026"
 * - Different months: "Mar 20 - May 3, 2026"
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a date range for display.
 * @param startDate - ISO date string (YYYY-MM-DD)
 * @param endDate - ISO date string (YYYY-MM-DD)
 * @returns Formatted date range string
 */
export function formatDateRange(startDate: string, endDate: string): string {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!start || !end) {
    return 'Invalid date';
  }

  const startMonth = MONTHS[start.month];
  const endMonth = MONTHS[end.month];

  // Same day
  if (start.year === end.year && start.month === end.month && start.day === end.day) {
    return `${startMonth} ${start.day}, ${start.year}`;
  }

  // Same month and year
  if (start.year === end.year && start.month === end.month) {
    return `${startMonth} ${start.day}-${end.day}, ${start.year}`;
  }

  // Same year, different months
  if (start.year === end.year) {
    return `${startMonth} ${start.day} - ${endMonth} ${end.day}, ${start.year}`;
  }

  // Different years (edge case)
  return `${startMonth} ${start.day}, ${start.year} - ${endMonth} ${end.day}, ${end.year}`;
}

/**
 * Parse an ISO date string into components.
 */
function parseDate(dateString: string): { year: number; month: number; day: number } | null {
  if (!dateString) return null;
  
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // 0-indexed
  const day = parseInt(parts[2], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  if (month < 0 || month > 11) return null;
  if (day < 1 || day > 31) return null;

  return { year, month, day };
}
